<template>
  <div v-if="canUseUpload">
    <HelpTip v-bind="labelInfo"></HelpTip>
    <van-field
      class="form-item-image work-temp-form-item"
      :name="id"
      :rules="[
        {
          required: required,
          message: languages.smartOrderPleaseChoose + label
        }
      ]"
    >
      <template #input>
        <van-uploader
          v-model="workFileArr"
          :accept="accept"
          :max-count="maxCount"
          :before-read="beforeRead"
          :after-read="afterRead"
          :preview-options="{
            closeable: true
          }"
          :before-delete="beforeDelete"
        >
          <template #preview-cover="{ customerType, url, duration }">
            <UploadVideoPreview
              v-if="customerType === 'video'"
              :firstFrameImage="url"
              :duration="duration"
            ></UploadVideoPreview>
          </template>
        </van-uploader>
        <IosUploadIcon v-if="showIOSUpload" @click="iosUpload"></IosUploadIcon>
      </template>
    </van-field>
    <p class="tip">{{ uploadTip }}</p>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import workTemp from '@mixins/officialSupport/workTemp';
import fileUpload, { acceptType } from '@mixins/officialSupport/fileUpload.js';
import bridge from '@utiles/bridge';
import UploadVideoPreview from '@components/officialSupport/UploadVideoPreview.vue';
import IosUploadIcon from '@components/officialSupport/IosUploadIcon.vue';
export default {
  name: 'TempUpload',
  mixins: [fileUpload, workTemp],
  props: {
    customKey: {
      type: Object,
      default: () => ({
        uploadType: ['image']
      })
    },
    /** 仅自助表单：按 uploadType 展示提示并放开视频上传（不依赖 isSupportVideoCompress） */
    selfServiceUpload: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      uploadType: [],
      uploadMode: 'image'
    };
  },
  computed: {
    ...mapState({
      languages: state => state.languages,
      letter: state => state.languages?.creatework,
      urlParams: state => state.urlParams,
      isUidLogin: state => Number(state.urlParams.isUidLogin) === 1,
      isUidGame: state => state.isUidGame,
      isLogin: state => state.isLogin,
      userInfo: state => state.userInfo
    }),
    uploadTypeLength() {
      return (this.uploadType || []).length;
    },
    isImageType() {
      return this.uploadTypeLength === 1 && this.uploadType.includes('image');
    },
    isVideoType() {
      return this.uploadTypeLength === 1 && this.uploadType.includes('video');
    },
    isImageAndVideoType() {
      return (
        this.uploadTypeLength === 2 &&
        this.uploadType.includes('image') &&
        this.uploadType.includes('video')
      );
    },
    uploadTip() {
      if (!this.selfServiceUpload) {
        return this.languages.creatework_uploadTips;
      }
      if (this.isVideoType) {
        return this.languages.videoUploadLimit;
      }
      if (this.isImageAndVideoType) {
        return [
          this.languages.creatework_uploadTips,
          this.languages.videoUploadLimit
        ].join('\n');
      }
      return this.languages.creatework_uploadTips;
    }
  },
  watch: {
    workFileArr: {
      handler(val) {
        const workFileArr = val || [];
        const imageFileArr =
          workFileArr?.filter(
            item => item.customerType === 'image' && item.status === 'done'
          ) || [];
        const videoFileArr =
          workFileArr?.filter(
            item => item.customerType === 'video' && item.status === 'done'
          ) || [];
        if (this.isImageType || this.isImageAndVideoType) {
          this.formData[this.id]['pic'] = JSON.stringify(
            imageFileArr.map(item => item.url)
          );
          this.formData[this.id]['pic_compress'] = JSON.stringify(
            imageFileArr.map(item => item.isCompress)
          );
        }
        if (this.isVideoType || this.isImageAndVideoType) {
          this.formData[this.id]['videoPath'] = JSON.stringify(
            videoFileArr.map(item => item.videoPath)
          );
          this.formData[this.id]['videoPreviewPic'] = JSON.stringify(
            videoFileArr.map(item => item.url)
          );
          this.formData[this.id]['videoDurations'] = JSON.stringify(
            videoFileArr.map(item => item.duration)
          );
        }
        this.formData[this.id]['picAndVideoSequence'] = JSON.stringify(
          workFileArr.map(item => {
            const url =
              item.customerType === 'image' ? item.url : item.videoPath;
            return url?.split('/')?.pop();
          })
        );
      },
      deep: true
    }
  },
  created() {
    this.formData[this.id] = {};
    if (this.selfServiceUpload) {
      const configured = Array.isArray(this.customKey.uploadType)
        ? this.customKey.uploadType.filter(t => t === 'image' || t === 'video')
        : [];
      this.uploadType = configured.length ? configured : ['image'];
      if (bridge.runInsdk()) {
        try {
          bridge.callSdkMethod('compressVideoConfig', this.VIDEO_CONFIG);
        } catch (error) {
          console.error(error);
        }
      }
    } else {
      this.uploadType = this.customKey.uploadType;
    }
    this.initFormData();
    this.handleUploadType();
  },
  methods: {
    // 自助表单：不依赖 isSupportVideoCompress，按 uploadMode 放行视频；其余走 mixin 原逻辑
    async beforeRead(file) {
      if (!this.selfServiceUpload) {
        return fileUpload.methods.beforeRead.call(this, file);
      }
      if (await this.oversize(file)) {
        return Promise.reject();
      }
      if (this.checkFileIsVideo(file)) {
        if (this.uploadMode === 'image') {
          this.$toast(this.languages?.creatework_uploadTips);
          return Promise.reject();
        }
        return Promise.resolve([file]);
      }
      return this.beforeReadImage(file);
    },
    // 自助表单：accept 跟随字段配置；旧模块仍受 openVideoUploadFunction 限制
    updateUploadType(uploadType) {
      if (!this.selfServiceUpload) {
        return fileUpload.methods.updateUploadType.call(this, uploadType);
      }
      this.uploadMode = uploadType;
      this.accept = acceptType[uploadType] || acceptType.image;
    },
    initFormData() {
      if (this.isImageType || this.isImageAndVideoType) {
        this.formData[this.id]['pic'] = '';
        this.formData[this.id]['pic_compress'] = '';
      }

      if (this.isVideoType || this.isImageAndVideoType) {
        this.formData[this.id]['videoPath'] = '';
        this.formData[this.id]['videoPreviewPic'] = '';
        this.formData[this.id]['videoDurations'] = '';
      }

      this.formData[this.id]['picAndVideoSequence'] = '';
    },
    handleUploadType() {
      if (this.isImageAndVideoType) {
        this.updateUploadType('imageAndVideo');
        return;
      }
      if (this.isImageType) {
        this.updateUploadType('image');
        return;
      }
      if (this.isVideoType) {
        this.updateUploadType('video');
      }
    }
  },
  components: {
    UploadVideoPreview,
    fileUpload,
    IosUploadIcon
  }
};
</script>

<style lang="scss" scoped>
@import '@assets/scss/officialSupport/workTempForm.scss';
.form-item-image {
  padding: 0;
  margin-bottom: 0;
  background-color: transparent;

  ::v-deep .van-field__control {
    flex-wrap: wrap;
  }

  ::v-deep .van-uploader__preview,
  ::v-deep .van-uploader__upload {
    overflow: hidden;
    background-color: var(--block-bg-light-color);
    border-radius: 6px;
  }

  ::v-deep .van-loading__spinner {
    vertical-align: baseline;
  }

  ::v-deep .van-uploader__upload-icon {
    color: var(--main-color);
  }
}
.tip {
  font-size: 12px;
  line-height: 14px;
  color: var(--placeholder-color);
  margin-bottom: 15px;
  white-space: pre-line;
}
</style>
